import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch, Ref, Emit } from 'vue-property-decorator';
import { CalendarEventDetail } from '@webcat/commons-web-components/dist/calendar/models/calendar-event-detail';
import { EdGenericCalendarWeekdays } from './interfaces/ed-generic-calendar-weekdays.interface';

@Component({
  name: 'ed-generic-calendar',
  components: {
  }
})
export default class EdGenericCalendar extends Vue {

  private readonly CUSTOM_EVENT_HEIGHT = '--custom-event-height';

  public static readonly VAR_CURRENT_DAY_FONT_COLOR = '--wc-current-day-font-color';
  public static readonly VAR_CURRENT_DAY_CONTAINER_BACKGROUND_COLOR = '--wc-current-day-container-background-color';
  public static readonly VAR_CURRENT_DAY_BUTTON_BACKGROUND_COLOR = '--wc-current-day-button-background-color';

  @Prop({ required: false, default: 'month' }) readonly mode: string;
  @Prop({ required: false, default: true }) readonly hideAddEvent: boolean;
  @Prop({ required: false, default: true }) readonly hideBtnToday: boolean;
  @Prop({ required: false, default: true }) readonly hideModeSelect: boolean;
  @Prop({ required: false, default: false }) readonly hideMonthNavigation: boolean;
  @Prop({ required: false, default: false }) readonly hideNavigation: boolean;
  @Prop({ required: false, default: null }) readonly minDate: string;
  @Prop({ required: false, default: null }) readonly start: string;
  @Prop({ required: false, default: null }) readonly calendarDays: Record<string, any>;
  @Prop({ required: false, default: null }) readonly events: Array<CalendarEventDetail>;
  @Prop({ required: false, default: null }) readonly originalEvents: Array<any>;
  @Prop({ required: false, default: null }) readonly eventSuffix: any;
  @Prop({ default: () => [0, 1, 2, 3, 4, 5, 6] }) readonly weekdays: number[] | EdGenericCalendarWeekdays[] ;
  @Prop({ required: false, default: null }) readonly hourStart: number;
  @Prop({ required: false, default: null }) readonly hourEnd: number;
  @Prop({ required: false, default: null }) readonly intervalHeight: number;
  @Prop({ required: false, default: false }) readonly hideDayNumbers: boolean;
  @Prop({ required: false, default: '100%' }) readonly height: string;
  @Prop({ required: false, default: false }) readonly hideSummary: boolean;
  @Prop({ required: false, default: false }) readonly hideHourlyTimeFrames: boolean;
  @Prop({ required: false, default: null }) readonly eventHeight: number;
  @Prop({ required: false, default: false }) readonly draggableEvents: boolean;
  @Prop({ required: false, default: null }) readonly draggableDates: string[];

  @Ref('wcCalendar') readonly wcCalendar: any;

  public isCoverVisible = false;
  
  public get calendarWeekdays() : number[] {
    return this.weekdays.map((item: number | EdGenericCalendarWeekdays) => {
      if (typeof item === 'number') {
        return item;
      }

      return item.day;
    });
  }

  public get year() : number {
    const arrayDate = this.start?.split('-');

    if (arrayDate && arrayDate.length >= 2) {
			return parseInt(arrayDate[0]);	
    }

    return null;
  }

  public get month() : number {
    const arrayDate = this.start?.split('-');

    if (arrayDate && arrayDate.length >= 2) {
			return parseInt(arrayDate[1]) - 1;
    }

    return null;
  }

  public get day() : number | null {
    const arrayDate = this.start?.split('-');
    return arrayDate?.length > 2 ? parseInt(arrayDate[2]) : null;
  }

  public get calendarStyle(): string {
    return this.eventHeight ? `${this.CUSTOM_EVENT_HEIGHT}: ${this.eventHeight}px;` : '';
  }

  mounted() {
    this._setCalendarDays(this.calendarDays);
    this._setEvents(this.events);
    this._setEventSuffix();
    this._setLabels();
    this._setDraggableDates();
  }

  @Watch('calendarDays', {immediate: true}) 
  calendarDaysChange() {
    this._setCalendarDays(this.calendarDays);
  }

  @Watch('events', {immediate: true}) 
  eventsChange() {
    this._setEvents(this.events);
  }

  @Watch('weekdays', { deep: true }) 
  weekdaysChange() {
    this._setLabels();
  }

  private _setCalendarDays(data: Record<string, any>) {
    if (data && this.wcCalendar?.setCalendarDays) {
      this.wcCalendar.setCalendarDays(data);
    }
  }

  private _setEvents(data: Array<any>) {
    if (data && this.wcCalendar?.setEvents) {
      this.$nextTick(() => {
        this._loadEventTemplate();
      });
      
    }
  }

  private _setEventSuffix() {
      if (this.wcCalendar?.setEventSuffix) {
        this.$nextTick(() => {
          this._loadEventSuffix();
        });
      }
    }
  
    private _loadEventSuffix() {
      setTimeout(() => {
        const suffixHtml = document.querySelector('.event-suffix')?.innerHTML;
        if (suffixHtml && this.eventSuffix) {
          this.eventSuffix.template = suffixHtml;
          this.wcCalendar.setEventSuffix(this.eventSuffix);
        }
      }, 500);
    }

  private _loadEventTemplate() {
    setTimeout(() => {
      if (this.events) {
        this.events.forEach(event => {
          if (event && event.uid) {
            const selector = `.custom-event-${event.uid}`;
            try {
              const customEventElement = document.querySelector(selector);
              if (customEventElement) {
                event.custom = event.custom || {};
                event.custom.template = customEventElement.innerHTML;
              }
            } catch {
              // Ignore custom event access errors for custom event elements.
            }
          }
        });
        this.wcCalendar?.setEvents(this.events);
      }
    }, 500);
  }

  private _setLabels() {
    const hasReplaceLabels = this.weekdays.some((item: number | EdGenericCalendarWeekdays) => {
      return typeof item === 'object';
    });

    if (hasReplaceLabels) {
      this.isCoverVisible = true;
      setTimeout(() =>{
        const weekDaysElements = this.wcCalendar
          .shadowRoot
          ?.querySelector('wc-calendar-week-header')
          ?.shadowRoot?.querySelector('.week-header')
          ?.querySelectorAll('.week-day');

        weekDaysElements.forEach((element: HTMLElement, index: number) => {
          const weekday = this.weekdays[index];
          if (weekday && typeof weekday === 'object') {
            element.innerHTML = weekday.label;
          }
        });
        this.isCoverVisible = false;
      }, 100);
    }
  }

  private _setDraggableDates() {
    if (this.draggableDates && this.wcCalendar?.setDraggableDates) {
      this.wcCalendar.setDraggableDates(this.draggableDates);
    }
  }

  @Emit('onWcCalendarDaySelection')
  public onWcCalendarDaySelection(customEvent: CustomEvent): CustomEvent {
    return customEvent;
  }

  @Emit('onWcCalendarDayMouseOver')
  public onWcCalendarDayMouseOver(customEvent: CustomEvent): CustomEvent {
    return customEvent;
  }

  @Emit('onWcCalendarDayMouseLeave')
  public onWcCalendarDayMouseLeave(customEvent: CustomEvent): CustomEvent {
    return customEvent;
  }

  @Emit('onWcCalendarEventSelection')
  public onWcCalendarEventSelection(customEvent: CustomEvent): CustomEvent {
    return customEvent;
  }

  @Emit('onWcCalendarEventMouseOver')
  public onWcCalendarEventMouseOver(customEvent: CustomEvent): CustomEvent {
    return customEvent;
  }

  @Emit('onWcCalendarEventMouseLeave')
  public onWcCalendarEventMouseLeave(customEvent: CustomEvent): CustomEvent {
    return customEvent;
  }

  @Emit('onWcCalendarDateChange')
  public onWcCalendarDateChange(customEvent: CustomEvent): CustomEvent {
    return customEvent;
  }

  @Emit('onWcCalendarEventDragStart')
  public onWcCalendarEventDragStart(customEvent: CustomEvent): CustomEvent {
    return customEvent;
  }

  @Emit('onWcCalendarEventDragEnd')
  public onWcCalendarEventDragEnd(customEvent: CustomEvent): CustomEvent {
    return customEvent;
  }

  @Emit('onWcCalendarDayContainerClick')
  public onWcCalendarDayContainerClick(customEvent: CustomEvent): CustomEvent {
    return customEvent;
  }

  @Emit('onWcCalendarDayButtonMouseOver')
  public onWcCalendarDayButtonMouseOver(customEvent: CustomEvent): CustomEvent {
    return customEvent;
  }

  @Emit('onWcCalendarDayButtonMouseLeave')
  public onWcCalendarDayButtonMouseLeave(customEvent: CustomEvent): CustomEvent {
    return customEvent;
  }

  public setCalendarDate(newDate: Date): void {
    this.wcCalendar?.setCalendarDate(newDate);
  }

  public getCalendarDate(): Date | null {
    return this.wcCalendar ? this.wcCalendar.getCalendarDate() : null;
  }

  public previousDate() {
    this.wcCalendar?.previousDate();
  }

  public nextDate() {
    this.wcCalendar?.nextDate();
  }
}
